import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { OutlineButton } from "components/buttons/OutlineButton";
import PaginationField from "components/Pagination";
import SearchInput from "components/SearchInput";
import {
  getLicenseTransactionTypeLabel,
  MSP_TRANSACTION_TYPE_OPTIONS,
  normalizeLicenseTransactionType,
} from "const/license";
import TransactionTypeDropdown from "pages/LicenseManagement/Transactions/components/TransactionTypeDropdown";
import { getDescription } from "pages/LicenseManagement/Transactions/const";
import iconMore from "assets/images/icons/icn_table_cell_more.png";
import iconRefresh from "assets/images/icons/icn_refresh.svg";
import { ReactComponent as CellSortIcon } from "assets/images/icons/btn_table_cell_sort.svg";
import { ReactComponent as CellSortDownIcon } from "assets/images/icons/btn_table_cell_sort_down.svg";
import { ReactComponent as CellSortUpIcon } from "assets/images/icons/btn_table_cell_sort_up.svg";

const RecentTransactionsBlock = ({
  transactions,
  onShowMore,
  onRefresh,
  title = "Recent Transactions (Last 10)",
  showControls = false,
  hideTitle = false,
}) => {
  const theme = useTheme();
  const [selectedTypes, setSelectedTypes] = useState(
    MSP_TRANSACTION_TYPE_OPTIONS.map((type) => type.value),
  );
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  const typeCounts = useMemo(() => {
    const counts = { all: transactions.length };
    transactions.forEach((transaction) => {
      const type = normalizeLicenseTransactionType(transaction.type);
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  }, [transactions]);

  const typeOptions = useMemo(
    () => MSP_TRANSACTION_TYPE_OPTIONS.map((type) => ({
      ...type,
      tabCount: typeCounts[type.value] || 0,
    })),
    [typeCounts],
  );

  const filteredTransactions = useMemo(() => {
    const allTypesSelected = selectedTypes.length === typeOptions.length;
    const search = searchText.trim().toLowerCase();

    return transactions.filter((transaction) => {
      const type = normalizeLicenseTransactionType(transaction.type);
      if (!allTypesSelected && !selectedTypes.includes(type)) return false;
      if (!search) return true;

      const searchableText = [
        dayjs.unix(transaction.timestamp).format("YYYY-MM-DD HH:mm:ss"),
        getLicenseTransactionTypeLabel(transaction.type),
        transaction.type,
        JSON.stringify(transaction.descriptionParams || transaction.metadata || {}),
      ].join(" ").toLowerCase();
      return searchableText.includes(search);
    });
  }, [transactions, selectedTypes, typeOptions.length, searchText]);

  useEffect(() => {
    setPage(0);
  }, [selectedTypes, searchText, sortBy, sortDirection, transactions]);

  const sortedTransactions = useMemo(() => {
    const valueFor = (transaction, key) => {
      switch (key) {
        case "date":
          return Number(transaction.timestamp) || 0;
        case "type":
          return getLicenseTransactionTypeLabel(transaction.type).toLowerCase();
        case "amount":
          return Number(transaction.change) || 0;
        case "balance":
          return Number(transaction.balance) || 0;
        case "description":
          return Object.entries(
            transaction.descriptionParams || transaction.metadata || {},
          )
            .map(([label, value]) => `${label}: ${value}`)
            .join(" ")
            .toLowerCase();
        default:
          return 0;
      }
    };

    return [...filteredTransactions].sort((a, b) => {
      const first = valueFor(a, sortBy);
      const second = valueFor(b, sortBy);
      const comparison = typeof first === "string"
        ? first.localeCompare(second)
        : first - second;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredTransactions, sortBy, sortDirection]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortDirection(key === "date" ? "desc" : "asc");
  };

  const displayedTransactions = showControls
    ? sortedTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : transactions;

  return (
    <Box sx={{ width: "100%" }}>
      <Stack spacing={hideTitle ? 0 : 2}>
        {!hideTitle && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ color: "#172664", fontSize: "15px", fontWeight: "bold" }}>
              {title}
            </Typography>
            {onShowMore && (
              <OutlineButton
                size="small"
                onClick={onShowMore}
                height="24px"
                startIcon={<img src={iconMore} alt="more" />}
                sx={{ fontSize: "12px", px: 1.5, borderRadius: "12px", color: "#172664", borderColor: "#172664" }}
              >
                Show More
              </OutlineButton>
            )}
          </Stack>
        )}

        {showControls && (
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            rowSpacing={2}
            sx={{ mb: 3 }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={{
                  mr: 2,
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#111",
                }}
              >
                Show
              </Typography>
              <TransactionTypeDropdown
                selectedTypes={selectedTypes}
                onChange={setSelectedTypes}
                options={typeOptions}
                totalCount={typeCounts.all || 0}
              />
            </Box>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Divider
                sx={{ width: "1px", height: "40px" }}
                orientation="vertical"
                flexItem
              />
              <SearchInput
                value={searchText}
                onChange={setSearchText}
                placeholder="Search by Date, Type"
                sx={{ width: "280px" }}
              />
              {onRefresh && (
                <Box
                  component="button"
                  type="button"
                  onClick={onRefresh}
                  aria-label="Reload transactions"
                  sx={{
                    alignItems: "center",
                    background: "none",
                    border: 0,
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "flex",
                    p: 1,
                    "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                  }}
                >
                  <img src={iconRefresh} alt="" />
                </Box>
              )}
            </Stack>
          </Grid>
        )}

        <TableContainer component={Box} sx={{ border: "none", boxShadow: "none" }}>
          <Table>
            <TableHead>
              <TableRow>
                {[
                  { label: "Date", key: "date" },
                  { label: "Type", key: "type" },
                  { label: "Amount", key: "amount" },
                  { label: "Description", key: "description" },
                  { label: "Balance After", key: "balance" },
                ].map(({ label, key }, index) => (
                  <TableCell
                    key={label}
                    align={index === 4 ? "right" : "left"}
                    sx={{ borderBottom: `1px solid ${theme.palette.divider}`, color: "#172664", fontWeight: "bold", fontSize: "14px", pb: 2, pl: index === 0 ? 0 : undefined, pr: index === 4 ? 0 : undefined }}
                  >
                    {showControls && key ? (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => handleSort(key)}
                        sx={{
                          alignItems: "center",
                          background: "none",
                          border: 0,
                          color: "inherit",
                          cursor: "pointer",
                          display: "flex",
                          font: "inherit",
                          fontWeight: "inherit",
                          gap: 0.5,
                          p: 0,
                        }}
                      >
                        {label}
                        {sortBy === key && sortDirection === "asc" ? (
                          <CellSortUpIcon />
                        ) : sortBy === key && sortDirection === "desc" ? (
                          <CellSortDownIcon />
                        ) : (
                          <CellSortIcon />
                        )}
                      </Box>
                    ) : label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedTransactions.length ? displayedTransactions.map((transaction, index) => {
                const change = Number(transaction.change) || 0;
                return (
                  <TableRow key={`${transaction.txn_id || transaction.txnId || transaction.timestamp}-${index}`} sx={{ bgcolor: index % 2 ? "#F9F9F9" : "transparent", "& .MuiTableCell-root": { border: "none", py: 1.5 } }}>
                    <TableCell sx={{ color: "rgba(0,0,0,0.65)", pl: 0 }}>{dayjs.unix(transaction.timestamp).format("YYYY-MM-DD HH:mm:ss")}</TableCell>
                    <TableCell>{getLicenseTransactionTypeLabel(transaction.type)}</TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: change > 0 ? "#008514" : "#373737" }}>{change > 0 ? `+${change.toLocaleString()}` : change.toLocaleString()}</TableCell>
                    <TableCell sx={{ color: "rgba(0,0,0,0.65)" }}>{getDescription(transaction)}</TableCell>
                    <TableCell align="right" sx={{ color: "rgba(0,0,0,0.65)", pr: 0 }}>{(Number(transaction.balance) || 0).toLocaleString()}</TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "rgba(0,0,0,0.45)" }}>No data available</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {showControls && (
          <Box mt={2} display="flex" justifyContent="center">
            <PaginationField
              name="MspLicenseTransactionsTable"
              pageCount={Math.max(1, Math.ceil(filteredTransactions.length / rowsPerPage))}
              pageIndex={page}
              pageSize={rowsPerPage}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setRowsPerPage(size);
                setPage(0);
              }}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default RecentTransactionsBlock;
